import currency from "currency.js";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";

import type {
  BookingData,
  TravellerType,
  TripDetails,
} from "@/features/trips/types/types";

type TravellerPriceMap = Record<TravellerType, number>;

export function useTripPrice(tripData: TripDetails) {
  const selectedTravellers = useWatch<BookingData, "travellers">({
    name: "travellers",
  });
  const isTripTourAdded = useWatch<BookingData, "isTripTourAdded">({
    name: "isTripTourAdded",
  });

  return useMemo(() => {
    const prices: TravellerPriceMap = {
      adultInDoubleRoom: tripData.priceForAdultInADoubleRoom || 0,
      adultInSingleRoom: tripData.priceForAdultInASingleRoom || 0,
      childWithBed: tripData.priceForChildWithBed || 0,
      childWithoutBed: tripData.priceForChildWithoutBed || 0,
      infant: tripData.priceForInfant || 0,
    };

    const discountedPrices: TravellerPriceMap = {
      adultInDoubleRoom: tripData.discountedPriceForAdultInADoubleRoom || 0,
      adultInSingleRoom: tripData.discountedPriceForAdultInASingleRoom || 0,
      childWithBed: tripData.discountedPriceForChildWithBed || 0,
      childWithoutBed: tripData.discountedPriceForChildWithoutBed || 0,
      infant: tripData.discountedPriceForInfant || 0,
    };

    const { basePrice, discountedBasePrice, tourPrice } = Object.entries(
      selectedTravellers,
    ).reduce(
      (acc, [travellerType, count]) => {
        const type = travellerType as TravellerType;

        return {
          basePrice: currency(prices[type], { precision: 3 })
            .multiply(count)
            .add(acc.basePrice).value,
          discountedBasePrice: currency(discountedPrices[type], {
            precision: 3,
          })
            .multiply(count)
            .add(acc.discountedBasePrice).value,
          tourPrice: currency(tripData.tourPrice ?? 0, { precision: 3 })
            .multiply(count)
            .add(acc.tourPrice).value,
        };
      },
      { basePrice: 0, discountedBasePrice: 0, tourPrice: 0 },
    );

    const discountAmount = currency(basePrice, { precision: 3 }).subtract(
      discountedBasePrice,
    ).value;

    let totalPrice = discountedBasePrice;
    if (isTripTourAdded) {
      totalPrice += tourPrice;
    }

    return {
      basePrice,
      discountAmount,
      tourPrice: isTripTourAdded ? tourPrice : undefined,
      totalPrice,
    };
  }, [selectedTravellers, tripData, isTripTourAdded]);
}
